import { useEffect } from 'react';

import { allowIdleReturnRefresh, holdBuildRefresh } from './web-build-refresh';

/**
 * While `active`, a newly deployed build waits instead of reloading the tab
 * (lib/web-build-refresh.ts). For work the user should not be PROMPTED about
 * but must not lose either — a payment in progress, a guided tour.
 *
 * A hold can only be shown to work on a build where the refresh itself works:
 * an earlier "the checkout holds it" check passed on a build that handed nothing
 * off for signed-in users at all, so it would have passed without the hold.
 * Prove the hand-off first, THEN the hold, on the same build.
 */
export function useHoldBuildRefresh(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    return holdBuildRefresh();
  }, [active]);
}

/**
 * The one way to say "leaving now would lose work". While `active`:
 *
 *  - the browser asks before a refresh or tab close (`beforeunload`), and
 *  - a newly deployed build does not reload the tab.
 *
 * These are one hook because they are one fact. Written separately, the second
 * is the one that gets forgotten — and an automatic reload destroys exactly
 * what the prompt was protecting, without asking.
 */
export function useUnsavedWorkGuard(active: boolean): void {
  useHoldBuildRefresh(active);

  useEffect(() => {
    if (!active) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Legacy browsers only show the prompt when returnValue is set.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [active]);
}

/**
 * Opt this page in to being moved onto a new build when the user comes back to
 * a tab that was hidden for a while (lib/web-build-refresh.ts). Only for pages
 * left open all day that keep nothing in memory a reload would lose — the
 * calendar, the till. Every other page moves at its next page change.
 */
export function useIdleReturnBuildRefresh(): void {
  useEffect(() => allowIdleReturnRefresh(), []);
}
